import { Collapse } from 'antd';
import Text from 'components/Text';
import { isNull } from 'lodash';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { IMAGES, INTERNAL_LINKS, STATUS_LIST } from '../../../enum';
import {
  checkIfStringContainsValue,
  getUserFullName,
  momentFormat,
  momentTimezone,
} from '../../../utils/common';
import { formatNumberWithCurrency } from '../../../utils/numbers';
import { scrollAppContainerToTop } from '../../../utils/window';
import { CustomTable } from '../..';
import ComplianceItemsList from '../../ComplianceItemsList';
import ProfileNamePicture from '../../shared-ui/ProfileNamePicture';
import StatusTag from '../../Tag/StatusTag';
import AmountTextReverseColors from '../../Text/AmountTextReverseColors';

const complianceDetailsKeysMap = {
  businessMileageCompliance: 'business-mileage',
  businessVehicleCompliance: 'business-vehicle',
  insuranceCompliance: 'insurance',
};

const ComplianceStatusTable = props => {
  const { t, dataSource, searchTerm, ...rest } = props;

  const filteredDataSource = useMemo(() => {
    let array = [...dataSource];

    if (searchTerm) {
      array = array.filter(item => {
        return checkIfStringContainsValue(getUserFullName(item), searchTerm);
      });
    }

    return array;
  }, [dataSource, searchTerm]);

  const COLUMNS = [
    {
      width: 250,
      defaultSortOrder: 'ascend',
      key: ['firstName', 'lastName'].join(','),
      title: t('name'),
      render: (_, data) => (
        <ProfileNamePicture user={data} redirectTo={INTERNAL_LINKS.COMPLIANCE_DETAILS(data._id)} />
      ),
      sorter: true,
    },
    {
      width: 200,
      key: 'group.name',
      title: t('groupName'),
      dataIndex: ['group', 'name'],
      sorter: true,
    },
    {
      width: 200,
      key: 'department',
      title: t('costCenter'),
      dataIndex: 'department',
      sorter: true,
      render: department => (
        <Text
          title={department}
          size="sm"
          style={{
            maxWidth: '170px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {department || '-'}
        </Text>
      ),
    },
    {
      width: 150,
      sorter: true,
      key: 'startDate',
      title: t('startDate'),
      dataIndex: 'startDate',
      render: startDate => (startDate ? momentFormat(startDate, 'DD MMM, YYYY') : '-'),
    },
    {
      sorter: true,
      key: 'complianceStatus',
      title: t('complianceStatus'),
      dataIndex: 'complianceStatus',
      render: (status, data) => <StatusTag status={status} />,
    },
    {
      width: 400,
      key: 'complianceDetails',
      title: t('nonComplianceDetails'),
      dataIndex: 'complianceDetails',
      render: complianceDetails => {
        if (complianceDetails) {
          const invalidDocuments = Object.keys(complianceDetails).filter(key => {
            const compliance = complianceDetails[key];
            return compliance.status === STATUS_LIST().Status.INVALID;
          });

          if (!invalidDocuments.length) return '-';

          return invalidDocuments.map(key => {
            const compliance = complianceDetails[key];
            return (
              <Collapse
                ghost
                expandIcon={({ isActive }) => (
                  <img
                    src={IMAGES.GREEN_CHEVRON_DOWN_ICON}
                    alt="expand icon"
                    style={{
                      transform: `translateY(-3px) rotate(${isActive ? 0 : 180}deg)`,
                    }}
                  />
                )}
              >
                <Collapse.Panel header={t(complianceDetailsKeysMap[key])} key={key}>
                  <ComplianceItemsList
                    t={t}
                    type={complianceDetailsKeysMap[key]}
                    complianceComponents={compliance.complianceComponents || compliance}
                  />
                </Collapse.Panel>
              </Collapse>
            );
          });
        }

        return '-';
      },
    },
    {
      width: 150,
      sorter: true,
      key: 'endOfGracePeriodDate',
      title: t('gracePeriodEndDate'),
      dataIndex: 'endOfGracePeriodDate',
      render: endOfGracePeriodDate =>
        endOfGracePeriodDate ? (
          <>
            <p>{`${momentFormat(endOfGracePeriodDate, 'DD MMM, YYYY')}`}</p>
            <p>{`(${momentTimezone(endOfGracePeriodDate).fromNow()})`}</p>
          </>
        ) : (
          '-'
        ),
    },
    {
      width: 250,
      sorter: true,
      key: 'lastAction.reason',
      title: t('lastAction'),
      dataIndex: ['lastAction', 'reason'],
      render: (reason, data) => (reason ? t(`${data.lastAction.type}-${reason}`) : '-'),
    },
    {
      width: 150,
      sorter: true,
      key: 'lastAction.created',
      title: t('lastActionDate'),
      dataIndex: ['lastAction', 'created'],
      render: created =>
        created ? (
          <>
            <p>{`${momentFormat(created, 'DD MMM, YYYY')}`}</p>
            <p>{`(${momentTimezone(created).fromNow()})`}</p>
          </>
        ) : (
          '-'
        ),
    },
    {
      width: 150,
      align: 'right',
      sorter: true,
      key: 'taxDue',
      title: t('taxDue'),
      dataIndex: 'taxDue',
      render: (taxDue, { _id, currency }) =>
        currency === 'CAD' ? (
          '-'
        ) : isNull(taxDue) ? (
          '-'
        ) : (
          <Link to={INTERNAL_LINKS.USER_TAX_REPORT(_id)} onClick={scrollAppContainerToTop}>
            <AmountTextReverseColors
              variant="b"
              amount={formatNumberWithCurrency(taxDue, currency)}
            />
          </Link>
        ),
    },
  ];

  return (
    <CustomTable
      {...rest}
      columns={COLUMNS}
      showSearchInput={false}
      dataSource={filteredDataSource}
      scroll={{ x: 1600 }}
    />
  );
};

export default ComplianceStatusTable;
